/* eslint-disable no-param-reassign */
import { endOfToday, startOfToday, subMonths } from 'date-fns';
import { produce } from 'immer';

import * as types from 'constants/ActionTypes';
import { MONTH } from 'constants/Global';

export const initialState = {
  data: {
    all: { apiError: {}, isFetching: {}, items: {}, links: {}, page: {} },
  },
  filters: {},
  queue: [],
  count: { value: null, isFetching: false, apiError: null },
  avgTime: { value: null, isFetching: false, apiError: null },
  audits: { items: [], isFetching: false, apiError: null, page: {}, links: {} },
  period: {
    from: subMonths(startOfToday(), 1).toISOString(),
    to: endOfToday().toISOString(),
    period: MONTH,
  },
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case types.FETCH_AUDIT_STATISTICS_REQUEST: {
      const { questionId } = action.params;

      draft.data.all.isFetching[questionId] = true;
      draft.data.all.apiError[questionId] = null;
      break;
    }

    case types.FETCH_AUDIT_STATISTICS_SUCCESS: {
      const { questionId } = action;

      draft.data.all.isFetching[questionId] = false;

      if (!draft.data.all.items[questionId]) {
        draft.data.all.items[questionId] = [];
      }

      if (JSON.stringify(draft.data.all.items[questionId]) !== JSON.stringify(action.auditStatistics.content)) {
        draft.data.all.items[questionId] = [...draft.data.all.items[questionId], ...action.auditStatistics.content];
        draft.data.all.links[questionId] = action.auditStatistics.links.reduce((acc, link) => {
          acc[link.rel] = link;
          return acc;
        }, {});
        draft.data.all.page[questionId] = action.auditStatistics.page;
      }
      break;
    }
    case types.FETCH_AUDIT_STATISTICS_FAILURE:
      if (action.id) {
        draft.data.all.isFetching[action.id] = false;
        draft.data.all.apiError[action.id] = action.error;
      }
      break;

    case types.FETCH_AUDIT_STATISTICS_COUNT_SUCCESS:
      draft.count.isFetching = false;
      draft.count.value = action.auditStatistics?.content?.[0]?.count;
      break;

    case types.FETCH_AUDIT_STATISTICS_AVG_TIME_SUCCESS:
      draft.avgTime.isFetching = false;
      draft.avgTime.value = action.payload.content?.[0]?.elapsedTime;
      break;

    case types.FETCH_AUDIT_STATISTICS_AVG_TIME_REQUEST:
      draft.avgTime.isFetching = true;
      draft.avgTime.apiError = null;
      break;

    case types.FETCH_AUDIT_STATISTICS_COUNT_REQUEST:
    case types.FETCH_AUDIT_STATISTICS_AUDITS_REQUEST:
      draft.audits.isFetching = true;
      draft.audits.apiError = null;
      break;

    case types.FETCH_AUDIT_STATISTICS_AUDITS_SUCCESS:
      draft.audits.isFetching = false;
      draft.audits.items = [...draft.audits.items, ...action.auditStatistics.content];
      draft.audits.links = action.auditStatistics.links.reduce((acc, link) => {
        acc[link.rel] = link;
        return acc;
      }, {});
      draft.audits.page = action.auditStatistics.page;
      break;

    case types.FETCH_AUDIT_STATISTICS_AVG_TIME_FAILURE:
      draft.avgTime.apiError = action.message;
      break;

    case types.FETCH_AUDIT_STATISTICS_COUNT_FAILURE:
    case types.FETCH_AUDIT_STATISTICS_AUDITS_FAILURE:
      draft.audits.apiError = action.error;
      break;

    case types.CLEAR_AUDIT_STATISTICS_AUDITS:
      draft.audits = {
        items: [],
        isFetching: false,
        apiError: {},
        page: {},
        links: {},
      };
      break;

    case types.CLEAR_AUDIT_STATISTICS:
      {
        draft.data.all.items = {};
        draft.data.all.isFetching = {};
        draft.data.all.apiError = {};
        draft.data.all.links = {};
        draft.data.all.page = {};

        draft.audits = {
          items: [],
          isFetching: false,
          apiError: {},
          page: {},
          links: {},
        };

        const defaultValues = {
          value: null,
          isFetching: false,
          apiError: {},
        };

        draft.count = defaultValues;
        draft.avgTime = defaultValues;
      }
      break;

    case types.CLEAR_AUDIT_STATISTIC:
      draft.data.all.items[action.id] = undefined;
      draft.data.all.isFetching[action.id] = undefined;
      draft.data.all.apiError[action.id] = undefined;
      draft.data.all.links[action.id] = undefined;
      draft.data.all.page[action.id] = undefined;
      break;

    case types.AUDIT_STATISTICS_ADD_FILTER:
      if (!draft.filters[action.id]) {
        draft.filters[action.id] = {};
      }

      if (!draft.filters[action.id][action.filter.property]) {
        draft.filters[action.id][action.filter.property] = [];
      }

      draft.filters[action.id][action.filter.property].push(action.filter.value);
      break;

    case types.AUDIT_STATISTICS_REMOVE_FILTER: {
      const { property, value: filterValue } = action.filter;
      draft.filters[action.id][property] = draft.filters[action.id][property].filter(value => value !== filterValue);

      if (draft.filters[action.id][property].length === 0) {
        const { [property]: omitted, ...properties } = draft.filters[action.id][property];
        draft.filters[action.id] = properties;
      }

      const withoutProperties = Object.keys(draft.filters[action.id]).length === 0;
      if (withoutProperties) {
        const { [action.id]: filterToRemove, ...filters } = draft.filters;
        draft.filters = filters;
      }
      break;
    }

    case types.AUDIT_STATISTICS_REMOVE_FILTERS:
      draft.filters = {};
      break;

    case types.SET_AUDIT_STATISTICS_PERIOD:
      draft.period = action.period;
      break;

    default:
      break;
  }

  return draft;
}, initialState);

export default reducer;
